import type { InfoPageContent } from './infoTypes';

export const guidesContent: InfoPageContent = {
  title: 'Guides',
  tagline: 'How to read the terminal: the two tables, the filters, the candidate cards, and the coin page.',
  lastUpdated: 'August 7, 2026',
  sections: [
    {
      heading: 'The two tables',
      paragraphs: [
        'The dashboard shows two focused tables. Predictor Candidates are deeply validated, fresh observations that passed every promotion gate, with their validation coverage and blockers visible. Eagle Watch shows developing, weakening, near-close, baseline-building, or otherwise unpromoted possibilities.',
        'A candidate lives in only one place at a time. Eagle Watch is the working queue; the Predictor is the promoted shortlist.',
      ],
    },
    {
      heading: 'Filters',
      bullets: [
        'Horizon — the analysis window used for detection and deep validation (e.g. 4H).',
        'Direction — long, short, or any.',
        'Market — the venue family to focus on.',
        'Search — narrow by symbol or name.',
        'Minimum quote volume — liquidity floor for participation.',
        'Minimum data quality — how strict the evidence gates are.',
      ],
      paragraphs: [
        'Your choices persist in the browser and survive navigation between pages.',
      ],
    },
    {
      heading: 'Reading a candidate card',
      bullets: [
        'Direction badge and family — e.g. long · hidden recovery — are frozen at original discovery.',
        'The score meter combines evidence strength and movement; it is context, not a verdict.',
        'The state bar shows the lifecycle stage and validation status (queued, partial, complete, failed).',
        'Support and resistance chips show the nearest tested levels and what holding or breaking them would mean.',
        'The driver box names what is currently moving the market.',
        'The clock meter shows candle maturity — a nearly finished candle blocks fresh promotion on the same horizon.',
      ],
    },
    {
      heading: 'The coin page',
      paragraphs: [
        'Search any USDT symbol from the header to open its full analysis page: a fluid chart with 1m through 1W timeframes, a timeframe matrix, candle study, historical breakthrough depth, dynamic support and resistance zones, funding behaviour, open interest, supply metrics, and contract-verified DEX participation.',
        'Current price, 24-hour change, and observed turnover are public market data with the venue clearly separated from the story.',
      ],
    },
    {
      heading: 'Timeframes and horizons',
      paragraphs: [
        'The selected horizon is the primary analysis window. Parent timeframes alter pressure and available space but never command the child timeframe. 1D and 1W remain wider pressure reflectors; short horizons are studied independently.',
      ],
    },
    {
      heading: 'Validation status',
      bullets: [
        'Queued — observation earned a deep-analysis slot and is waiting or in progress.',
        'Partial — some evidence resolved; promotion may be blocked on coverage.',
        'Complete — full deep validation resolved for the selected hierarchy.',
        'Failed — candle history or analysis failed; the candidate stays in Eagle Watch and is never silently promoted.',
      ],
    },
    {
      heading: 'Why the Predictor can be empty',
      paragraphs: [
        'An empty Predictor is a deliberate state. The interface states the concrete reason: still building baselines, deep history failed, near-close timing blocked, or specific evidence blockers. Pilum abstains instead of forcing a prediction from insufficient evidence.',
      ],
    },
    {
      heading: 'Local candidate history',
      paragraphs: [
        'The coin page keeps a local archive of that symbol’s candidate episodes and meaningful state changes in your browser’s IndexedDB. Original discovery price, direction, and family are frozen per episode; current price and movement update separately. Clearing your browser data removes the archive — Pilum keeps no copy anywhere else.',
      ],
    },
  ],
};
